import { useState, useEffect, useRef, ReactNode } from 'react';
import { Button, Loading, Modal, Paragraph, useToast } from '@aidkitorg/component-library';
import { usePost } from '../API';

interface DevTool {
  name: string;
  description: string;
}

type JobStatus = 'QUEUED' | 'STARTED' | 'FINISHED' | 'FAILED' | 'NOT_FOUND';

interface JobState {
  taskId: string;
  status: JobStatus;
  result: { success: boolean; error?: string } | null;
  startedAt: string | null;
  finishedAt: string | null;
  erroredAt: string | null;
}

const TERMINAL_STATUSES: JobStatus[] = ['FINISHED', 'FAILED', 'NOT_FOUND'];
const VALID_STATUSES: JobStatus[] = ['QUEUED', 'STARTED', 'FINISHED', 'FAILED', 'NOT_FOUND'];
const isJobStatus = (s: string): s is JobStatus => VALID_STATUSES.includes(s as JobStatus);

function formatDuration(startedAt: string | null, finishedAt: string | null, erroredAt: string | null): string | null {
  const end = finishedAt ?? erroredAt;
  if (!startedAt || !end) return null;
  const ms = new Date(end).getTime() - new Date(startedAt).getTime();
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const minutes = Math.floor(ms / 60_000);
  const seconds = Math.round((ms % 60_000) / 1000);
  return `${minutes}m ${seconds}s`;
}

export const DevToolsPage = () => {
  const [tools, setTools] = useState<DevTool[] | null>(null);
  const [triggering, setTriggering] = useState<Set<string>>(new Set());
  const [jobStates, setJobStates] = useState<Map<string, JobState>>(new Map());
  const [confirmationModal, setConfirmationModal] = useState<DevTool | null>(null);
  const { toast } = useToast();

  const listTools = usePost('/dev/tools/list');
  const triggerTool = usePost('/dev/tools/trigger');
  const getJobResult = usePost('/dev/tools/job_result');

  // Stable ref so the polling effect closure doesn't capture stale getJobResult
  const getJobResultRef = useRef(getJobResult);
  getJobResultRef.current = getJobResult;

  useEffect(() => {
    listTools({} as never).then(response => {
      if (response && Array.isArray(response.tools)) {
        setTools(response.tools);
      }
    });
  }, []);

  // Poll in-flight jobs every 2s
  useEffect(() => {
    const inFlight = Array.from(jobStates.entries()).filter(
      ([, j]) => !TERMINAL_STATUSES.includes(j.status),
    );
    if (inFlight.length === 0) return;

    const timer = setTimeout(async () => {
      for (const [toolName, job] of inFlight) {
        try {
          const response = await getJobResultRef.current({ taskId: job.taskId });
          const rawStatus: string = response.status ?? '';
          const nextStatus: JobStatus = isJobStatus(rawStatus) ? rawStatus : 'STARTED';
          setJobStates(prev => {
            const next = new Map(prev);
            next.set(toolName, {
              ...job,
              status: nextStatus,
              result: response.result ?? null,
              startedAt: response.startedAt ?? null,
              finishedAt: response.finishedAt ?? null,
              erroredAt: response.erroredAt ?? null,
            });
            return next;
          });
        } catch {
          // network error — keep polling
        }
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [jobStates]);

  const confirmTrigger = async () => {
    if (!confirmationModal) return;
    const { name } = confirmationModal;
    setConfirmationModal(null);
    setTriggering(prev => new Set(prev).add(name));
    setJobStates(prev => { const next = new Map(prev); next.delete(name); return next; });

    try {
      const response = await triggerTool({ toolName: name });
      if ('error' in response) {
        toast({ title: 'Failed to queue tool', description: response.error, variant: 'error' });
      } else {
        setJobStates(prev => new Map(prev).set(name, {
          taskId: response.taskId,
          status: 'QUEUED',
          result: null,
          startedAt: null,
          finishedAt: null,
          erroredAt: null,
        }));
      }
    } catch (err) {
      toast({ title: 'Failed to queue tool', description: err instanceof Error ? err.message : String(err), variant: 'error' });
    } finally {
      setTriggering(prev => { const next = new Set(prev); next.delete(name); return next; });
    }
  };

  if (tools === null) return <Loading />;

  return (
    <>
      {confirmationModal && (
        <Modal
          open
          onClose={() => setConfirmationModal(null)}
          title={`Run ${confirmationModal.name}`}
        >
          <Paragraph measure="wide">
            This will run <strong>{confirmationModal.name}</strong> across all programs.
            {confirmationModal.description && <><br /><br />{confirmationModal.description}</>}
            <br /><br />
            Are you sure?
          </Paragraph>
          <div className="flex flex-row justify-end gap-x-3">
            <Button variant="secondary" onClick={() => setConfirmationModal(null)}>Cancel</Button>
            <Button variant="danger" onClick={confirmTrigger}>Run now</Button>
          </div>
        </Modal>
      )}

      <div className="px-4 sm:px-6 lg:px-8">
        <div className="mt-8 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr>
                    <Header content="Tool" />
                    <Header content="Description" />
                    <Header content="Status" />
                    <Header content="Duration" />
                    <th scope="col" className="py-3 pl-4 pr-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500 sm:pl-1" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {tools.map(tool => {
                    const isTriggering = triggering.has(tool.name);
                    const job = jobStates.get(tool.name);
                    const isInFlight = job ? !TERMINAL_STATUSES.includes(job.status) : false;
                    const isBusy = isTriggering || isInFlight;
                    const duration = job ? formatDuration(job.startedAt, job.finishedAt, job.erroredAt) : null;

                    return (
                      <tr key={tool.name}>
                        <DataCell content={<span className="font-mono">{tool.name}</span>} />
                        <DataCell content={tool.description ?? ''} />
                        <DataCell content={<StatusCell job={job} isTriggering={isTriggering} />} />
                        <DataCell content={duration ?? '—'} />
                        <DataCell content={
                          <Button variant="secondary" onClick={() => setConfirmationModal(tool)} disabled={isBusy}>
                            {isTriggering ? 'Queuing…' : 'Run now'}
                          </Button>
                        } />
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

function StatusCell({ job, isTriggering }: { job: JobState | undefined; isTriggering: boolean }) {
  if (isTriggering) return <span className="text-gray-400">Queuing…</span>;
  if (!job) return <span className="text-gray-400">—</span>;
  switch (job.status) {
    case 'FINISHED':
      if (job.result?.success) return <span className="text-green-600 font-medium">Finished</span>;
      return <span className="text-red-600 font-medium" title={job.result?.error}>Failed ({job.result?.error ?? 'unknown error'})</span>;
    case 'FAILED':
      return <span className="text-red-600 font-medium" title={job.result?.error}>Failed ({job.result?.error ?? 'unknown error'})</span>;
    case 'STARTED':
      return <span className="text-blue-600 font-medium">Running…</span>;
    case 'NOT_FOUND':
      return <span className="text-red-600 font-medium">Not found</span>;
    default:
      return <span className="text-yellow-600 font-medium">Queued</span>;
  }
}

function Header(props: { content: string }) {
  return (
    <th scope="col" className="py-3 pl-4 pr-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500 sm:pl-1">
      {props.content}
    </th>
  );
}

function DataCell(props: { content: ReactNode }) {
  return (
    <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-1">
      {props.content}
    </td>
  );
}
